import { ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { UseInfiniteQueryResult } from '@tanstack/react-query';
import { useFormContext } from 'react-hook-form';
import { isString } from 'fp-ts/string';
import { Selected } from '@optimus-web/core';
import { mapId } from '@mews-commander/utils';
import { FormAsyncSelectFieldProps } from '@mews/framework';
import { BaseAsyncSelectEntity } from './types';
import { normalizeSelectedValueToArray } from './utils';

interface Params<T> {
    useSearchQuery: (searchToken: string) => UseInfiniteQueryResult<T[]>;
    useGetDefaultQuery: (ids: string[]) => T[];
    name: string;
    multiselect?: boolean;
}

interface Result<T> extends Pick<FormAsyncSelectFieldProps<T[]>, 'onChange' | 'useDataQuery' | 'defaultValue'> {
    addSelectedItem: (newOption: T) => void;
    selectedItems: T[];
}

export const useAsyncSelectFieldControls = <T extends BaseAsyncSelectEntity>({
    useSearchQuery,
    useGetDefaultQuery,
    name,
    multiselect,
}: Params<T>): Result<T> => {
    const { setValue, watch, getValues } = useFormContext<{ [K in typeof name]: Selected }>();
    const itemsIds = watch(name);
    const [selectedItems, setSelectedItems] = useState<T[]>([]);
    const searchCache = useRef<T[]>([]);

    const memoizedSelectedItemsIds = useMemo(() => mapId(selectedItems), [selectedItems]);

    const useSearchResults = (searchToken: string) => {
        const { data, ...rest } = useSearchQuery(searchToken);

        const filteredData = multiselect
            ? (data || []).filter((item: T) => !memoizedSelectedItemsIds.includes(item.id))
            : data;

        searchCache.current = filteredData || [];

        return { ...rest, data: filteredData };
    };

    const defaultSelectedItemIds = useMemo(() => normalizeSelectedValueToArray(getValues(name)), [getValues, name]);
    const defaultItems = useGetDefaultQuery(defaultSelectedItemIds);

    useEffect(() => {
        if (defaultSelectedItemIds.length > 0 && defaultItems.length > 0) {
            setSelectedItems(defaultItems);
        }
    }, [defaultSelectedItemIds, defaultItems]);

    const onChange = (_event: ChangeEvent, newEntities: Selected) => {
        if (!multiselect) {
            if (newEntities && isString(newEntities)) {
                const selectedItem = searchCache.current.find((item) => item.id === newEntities);
                if (selectedItem) {
                    setSelectedItems([selectedItem]);
                }
            } else {
                setSelectedItems([]);
            }
            return;
        }

        // Multiselect
        if (Array.isArray(newEntities) && Array.isArray(itemsIds)) {
            const newEntityIds = new Set(newEntities);
            const currentEntityIds = new Set(itemsIds);

            if (newEntityIds.size > currentEntityIds.size) {
                // Add items
                setSelectedItems((prev) => {
                    const existingItemIds = new Set(prev.map((item) => item.id));
                    const itemIdsToAdd = newEntityIds.difference(existingItemIds);
                    const newItemsToAdd = searchCache.current.filter((item) => itemIdsToAdd.has(item.id));

                    if (newItemsToAdd.length === 0) return prev;

                    return [...prev, ...newItemsToAdd];
                });
            } else {
                // Remove items
                setSelectedItems((prev) => prev.filter((item) => newEntities.includes(item.id)));
            }
        }
    };

    const addSelectedItem = (item: T) => {
        setSelectedItems((prev) => {
            const updated = [...prev, item];
            setValue(name, multiselect ? mapId(updated) : item.id);
            return updated;
        });
    };

    return {
        selectedItems,
        defaultValue: multiselect ? [] : null,
        useDataQuery: useSearchResults,
        onChange,
        addSelectedItem,
    };
};
